import { useCallback, useRef } from 'react';

import { useActor } from '@xstate/react';

import { useTelemetry } from '@order/Telemetry';

import { useGlobalAppState } from '../../../context/GlobalAppState/GlobalAppState';
import { useCloseBag } from '../../useBagNavigation/useBagNavigation';
import { useReorderNavigation } from '../../useReorderNavigation/useReorderNavigation';
import { reorderIfRequiredLogger as logger } from '../useReorderIfRequired.logger';
import { checkIfJustStartedReorder, getValidReorderEventKeys } from '../useReorderIfRequired.utils';

import type { ReorderIfRequiredEvent } from '../useReorderIfRequired.types';

/**
 * Returns a reference to reorder `confirmLocation` helper to avoid extra rerenders.
 */
export const useMemoizedConfirmReorderLocation = () => {
  const { reorderMachineRef } = useGlobalAppState();
  const [state, sendReorderEvent] = useActor(reorderMachineRef);
  const { track } = useTelemetry();
  const { navigateToConfirmLocation, replaceWithConfirmLocation } = useReorderNavigation();
  const closeBag = useCloseBag();

  // ─── Handlers ────────────────────────────────────────────────────────────────

  const confirmLocation = useCallback(
    (event?: ReorderIfRequiredEvent, options?: { referrer?: string }) => {
      logger.info('Confirm Location', event);

      sendReorderEvent({
        ...getValidReorderEventKeys(event),
        type: 'CONFIRM_LOCATION',
      });

      const navParams = options?.referrer ? { referrer: options.referrer } : undefined;

      if (checkIfJustStartedReorder(state.value)) {
        track('reorder.start', {
          ...getTelemetryData(event ?? state.context),
          startingFrom: 'confirm_location',
          userInitiated: event?.userInitiated,
        });

        closeBag();
        setTimeout(() => navigateToConfirmLocation(navParams));

        return;
      }

      replaceWithConfirmLocation(navParams);
    },
    [
      state.value,
      state.context,
      sendReorderEvent,
      closeBag,
      navigateToConfirmLocation,
      replaceWithConfirmLocation,
      track,
    ],
  );
  const confirmLocationRef = useRef(confirmLocation);

  confirmLocationRef.current = confirmLocation;

  return confirmLocationRef;
};

// ─── Helpers ────────────────────────────────────────────────────────────────────

function getTelemetryData(event: ReorderIfRequiredEvent) {
  return {
    orderId: event.orderId,
    lineItemId: event.lineItemId,
    restaurantId: event.restaurantId,
    restaurantSlug: event.restaurantSlug,
    orderType: event.orderType,
    orderTotal: event.orderTotal,
    reorderResult: event.reorderResult,
    conflictType: event.conflictType,
  };
}
